import { Controller } from "@hotwired/stimulus";

/**
 * 商品一覧ページのメイン機能を管理するStimulusコントローラー
 *
 * 使用場所:
 * - 商品一覧ページ（/products）
 *
 * 主な機能:
 * - 商品詳細モーダルの表示・非表示制御
 * - カート追加モーダルの表示・非表示制御
 * - カート確認ドロワーの開閉制御（ボタンクリックによる）
 * - 期限切れ商品モーダルの表示制御
 * - 注文確認モーダルの表示制御
 * - 背景スクロール制御とオーバーレイ管理
 * - カートサマリーボタンの状態管理
 *
 * 必要なターゲット要素:
 * - weekTab: 週選択タブ
 * - content: モーダルコンテンツ
 * - confirm: カート確認ドロワー
 * - cartButton: カートボタン
 * - actionButtons: アクションボタン群
 * - expiredCartModal: 期限切れカートモーダル
 * - orderConfirmationModal: 注文確認モーダル
 * - productDetailModal: 商品詳細モーダル
 */
export default class extends Controller {
  static targets = [
    "weekTab",
    "content",
    "confirm",
    "cartButton",
    "actionButtons",
    "expiredCartModal",
    "orderConfirmationModal",
    "productDetailModal",
  ];
  static values = {
    drawerOpen: Boolean,
  };

  connect() {
    // コントローラー接続時の初期化処理
    this.updateCartSummaryButtons();

    // 既存のオーバーレイを確認して削除（前回のオーバーレイが残っている場合）
    this.cleanupExistingOverlay();

    // ドロワーが開く状態の場合、アニメーション無しで開いた状態にする
    if (this.hasDrawerOpenValue && this.drawerOpenValue) {
      // カートサマリーのz-indexを開いた状態に変更
      const cartSummary = document.getElementById("cart-summary");
      if (cartSummary) {
        cartSummary.classList.remove("z-10");
        cartSummary.classList.add("z-13");
      }

      // アニメーション無しで直接開いた状態に設定
      this.confirmTarget.classList.add("flex");
      this.confirmTarget.classList.remove("h-0");
      this.confirmTarget.classList.add("h-[70vh]");
      this.confirmTarget.classList.add("is-drawer-open");
      this.updateCartSummaryButtons(true);

      // オーバーレイを作成・表示
      this.createOverlay();

      // 背景スクロールを無効化
      document.body.style.overflow = "hidden";
    }
  }

  // モーダル表示
  show() {
    this.contentTarget.hidden = false;
    this.contentTarget.querySelector("#add-to-cart-modal-content").focus();

    // cart_summaryドロワーの実際の表示状態を直接確認
    const confirmTarget = document.querySelector(
      '[data-products-target="confirm"]'
    );
    const isDrawerCurrentlyOpen =
      confirmTarget && confirmTarget.classList.contains("is-drawer-open");

    // hidden fieldに実際の状態を設定
    const drawerOpenField = document.querySelector("#drawer-open-field");
    if (drawerOpenField) {
      drawerOpenField.value = isDrawerCurrentlyOpen ? "true" : "false";
    }

    // 背景スクロールを無効化
    document.body.style.overflow = "hidden";
  }

  // モーダル非表示
  hide() {
    this.contentTarget.hidden = true;
    const parent = this.contentTarget.querySelector(
      "#add-to-cart-modal-content"
    );
    parent.innerHTML = "";
    // 背景スクロールを復元
    document.body.style.overflow = "";
  }

  // カート確認ドロワー表示
  showConfirm() {
    // カートサマリーのz-indexを開いた状態に変更
    const cartSummary = document.getElementById("cart-summary");
    if (cartSummary) {
      cartSummary.classList.remove("z-10");
      cartSummary.classList.add("z-13");
    }

    // オーバーレイを作成・表示
    this.createOverlay();

    this.confirmTarget.classList.add("flex");
    this.confirmTarget.classList.remove("h-0");
    this.confirmTarget.classList.add("h-[70vh]");
    this.confirmTarget.classList.add("is-drawer-open"); // 判定用クラスを追加
    this.updateCartSummaryButtons(true); // ドロワーが開いた状態
    // 背景スクロールを無効化
    document.body.style.overflow = "hidden";
  }

  // カート確認ドロワー非表示（外クリック・スワイプから呼ばれる）
  hideConfirm() {
    try {
      // 現在のDOMから直接ドロワー要素を取得
      const confirmTarget = document.querySelector(
        '[data-products-target="confirm"]'
      );

      if (!confirmTarget) {
        return; // オーバーレイは削除しない
      }

      // ドロワーが実際に開いているかチェック
      if (!confirmTarget.classList.contains("is-drawer-open")) {
        return; // オーバーレイは削除しない
      }

      // ドロワーを閉じる
      confirmTarget.classList.remove("h-[70vh]");
      confirmTarget.classList.add("h-0");
      confirmTarget.classList.remove("is-drawer-open");

      this.updateCartSummaryButtons(false);

      // カートサマリーのz-indexを閉じた状態に変更
      const cartSummary = document.getElementById("cart-summary");
      if (cartSummary) {
        cartSummary.classList.remove("z-13");
        cartSummary.classList.add("z-10");
      }

      // 背景スクロールを復元
      document.body.style.overflow = "";

      setTimeout(() => {
        const currentConfirmTarget = document.querySelector(
          '[data-products-target="confirm"]'
        );
        if (currentConfirmTarget) {
          currentConfirmTarget.classList.remove("flex");
        }
      }, 300);

      // ドロワーが正常に閉じられた場合のみオーバーレイを削除
      this.removeOverlay();
    } catch (error) {
      return;
    }
  }

  // 期限切れ商品モーダル非表示
  hideExpiredCartModal() {
    if (this.hasExpiredCartModalTarget) {
      this.expiredCartModalTarget.hidden = true;
    }
    // 背景スクロールを復元
    document.body.style.overflow = "";
  }

  // 注文確認モーダル非表示
  hideOrderConfirmationModal() {
    if (this.hasOrderConfirmationModalTarget) {
      this.orderConfirmationModalTarget.hidden = true;
    }
    // 背景スクロールを復元
    document.body.style.overflow = "";

    // URLを /products にリセット
    this.resetUrlToProducts();
  }

  // URLを /products にリセット
  resetUrlToProducts() {
    // URLを /products に置き換え（ページリロードなし）
    window.history.replaceState({}, "", "/products");
  }

  // 商品詳細モーダル表示
  showProductDetail() {
    // Stimulusターゲットを使用（通常の商品一覧から）
    if (this.hasProductDetailModalTarget) {
      this.productDetailModalTarget.hidden = false;
      // 背景スクロールを無効化
      document.body.style.overflow = "hidden";
    } else {
      // ターゲットが見つからない場合は直接DOM操作（カート内から）
      const modal = document.querySelector("#product-detail-modal");
      if (modal) {
        modal.hidden = false;
        // 背景スクロールを無効化
        document.body.style.overflow = "hidden";
      }
    }
  }

  // 商品詳細モーダル非表示
  hideProductDetail() {
    if (this.hasProductDetailModalTarget) {
      this.productDetailModalTarget.hidden = true;
      // 背景スクロールを復元
      document.body.style.overflow = "";
    } else {
      // ターゲットが見つからない場合は直接DOM操作
      const modal = document.querySelector("#product-detail-modal");
      if (modal) {
        modal.hidden = true;
        // 背景スクロールを復元
        document.body.style.overflow = "";
      }
    }
  }

  // 商品詳細モーダルを閉じてから数量選択モーダルを開く
  hideProductDetailAndShowCart(event) {
    // 商品詳細モーダルを閉じる
    this.hideProductDetail();

    // 少し遅延させてから数量選択モーダルを開く
    setTimeout(() => {
      this.show();
    }, 100);
  }

  // 期限切れ商品モーダルから注文確認ドロワーを開く
  showConfirmFromExpiredModal() {
    // 期限切れモーダルを閉じる
    this.hideExpiredCartModal();
    // 注文確認ドロワーを開く
    this.showConfirm();
  }

  // ドロワー下部ボタンの表示切り替え
  updateCartSummaryButtons(isDrawerOpen = null) {
    // 現在のDOMから直接ボタン要素を取得
    const cartButton = document.querySelector(
      '[data-products-target="cartButton"]'
    );
    const actionButtons = document.querySelector(
      '[data-products-target="actionButtons"]'
    );

    if (cartButton && actionButtons) {
      // 引数で状態が指定されていない場合は実際の状態を確認
      if (isDrawerOpen === null) {
        const confirmTarget = document.querySelector(
          '[data-products-target="confirm"]'
        );
        isDrawerOpen =
          confirmTarget && confirmTarget.classList.contains("is-drawer-open");
      }

      cartButton.hidden = isDrawerOpen;
      actionButtons.hidden = !isDrawerOpen;
    }
  }

  // ドロワー内クリック時の閉じるイベントを防ぐ
  preventClose(event) {
    event.stopPropagation();
  }

  // オーバーレイを作成
  createOverlay() {
    // DOM内に既存のオーバーレイがないかチェック
    const existingOverlay = document.querySelector(
      ".fixed.inset-0.bg-black\\/20.z-12"
    );
    if (existingOverlay) {
      this.overlay = existingOverlay;
      return;
    }
    if (this.overlay) {
      return; // 既に存在する場合は何もしない
    }
    this.overlay = document.createElement("div");
    this.overlay.className =
      "fixed inset-0 bg-black/20 z-12 transition-opacity duration-300 ease-in-out";
    this.overlay.addEventListener("click", () => {
      this.hideConfirm();
    });

    document.body.appendChild(this.overlay);

    // アニメーション用に少し遅延してopacityを設定
    setTimeout(() => {
      if (this.overlay) {
        this.overlay.style.opacity = "1";
      }
    }, 10);
  }

  // オーバーレイを削除
  removeOverlay() {
    if (!this.overlay) return;
    this.overlay.style.opacity = "0";
    setTimeout(() => {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
        this.overlay = null;
      }
    }, 300);
  }

  // 既存のオーバーレイをクリーンアップ
  cleanupExistingOverlay() {
    // 注文確定の通信中オーバーレイが残っていれば削除
    // （注文失敗時はHTTP 200でcart-summaryが再描画され、turbo:submit-endでは解除されないため）
    const blockingOverlay = document.getElementById("order-submit-overlay");
    if (blockingOverlay && blockingOverlay.parentNode) {
      blockingOverlay.parentNode.removeChild(blockingOverlay);
    }

    // DOM内の孤立したオーバーレイを探して削除
    const existingOverlays = document.querySelectorAll(".fixed.inset-0.z-20");
    existingOverlays.forEach((overlay) => {
      // オーバーレイ要素の特徴的なクラス構成をチェック
      if (
        overlay.className.includes("bg-black") &&
        overlay.className.includes("transition-opacity")
      ) {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }
    });
    // オーバーレイ参照をリセット
    this.overlay = null;
    // 背景スクロールを復元
    document.body.style.overflow = "";
  }

  // フォーム送信前のバリデーション
  validateAndSubmit(event) {
    const modal = document.querySelector("#add-to-cart-modal-content");
    const cartControllers = modal.querySelectorAll('[data-controller="cart"]');
    let hasError = false;

    cartControllers.forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "cart"
      );
      if (controller && controller.isValid === false) {
        hasError = true;
      }
    });

    if (hasError) {
      event.preventDefault();
      return false;
    }

    // cart_summaryドロワーの実際の表示状態を直接確認してhidden fieldに反映
    const confirmTarget = document.querySelector(
      '[data-products-target="confirm"]'
    );
    const isDrawerCurrentlyOpen =
      confirmTarget && confirmTarget.classList.contains("is-drawer-open");

    const drawerOpenField = modal.querySelector('[name="drawer_open"]');
    if (drawerOpenField) {
      drawerOpenField.value = isDrawerCurrentlyOpen ? "true" : "false";
    }

    // バリデーション通過時はモーダルを閉じる（少し遅延を設ける）
    setTimeout(() => {
      this.hide();
    }, 500);
  }
}
